import { PHASE, PLAYER } from "../../../common/constants.ts";
import {
  getDashboardContractStatus,
  type DashboardContractStatus,
} from "../../../common/dashboardContractStatus.ts";
import { getBestPlayerBoxScore } from "../../../common/getBestPlayerBoxScore.ts";
import { deriveGameAttribution } from "../../../common/gameAttribution.ts";
import { season, team } from "../core/index.ts";
import { idb } from "../db/index.ts";
import { g, getProcessedGames, helpers, orderTeams } from "../util/index.ts";
import type { UpdateEvents } from "../../../common/types.ts";
import { getUpcoming } from "./dailySchedule.ts";
import { getMaxPlayoffSeed } from "./standings.ts";
import { processEvents } from "./news.ts";
import addFirstNameShort from "../util/addFirstNameShort.ts";

// Home rotates through a compact recent-news ticker. The selected headline opens
// its detail in place, so this slice only needs enough depth for useful rotation.
const HOME_NEWS_LIMIT = 12;

type DashboardAction = {
  labelKey: string;
  defaultLabel: string;
  path: string[];
};

type PhaseTask = {
  phase: number;
  titleKey: string;
  defaultTitle: string;
  descriptionKey: string;
  defaultDescription: string;
  primaryAction: DashboardAction;
  secondaryAction?: DashboardAction;
};

type RiskAlert = {
  key: string;
  severity: "danger" | "warning" | "info";
  titleKey: string;
  defaultTitle: string;
  detailKey: string;
  defaultDetail: string;
  detailParams?: Record<string, number | string>;
  path: string[];
  // R1-2: after arriving at `path`, the UI expands this sheet via the generic
  // `?m_open=<sheetKey>` intent channel (kept in lock-step with the UI
  // DashboardRiskAlert type). `path` stays the page, `openSheet` the on-arrival
  // sheet. Phase-aware market landings (roster-short) are resolved UI-side by
  // concept — the worker bundle must not import UI navigation.
  openSheet?: string;
};

export const getPhaseTask = (phase: number): PhaseTask | undefined => {
  const tasks: Record<number, PhaseTask> = {
    [PHASE.EXPANSION_DRAFT]: {
      phase,
      titleKey: "phaseHighlight.expansionDraftTitle",
      defaultTitle: "Expansion draft in progress",
      descriptionKey: "phaseHighlight.expansionDraftDesc",
      defaultDescription: "Protect the right players and get expansion rosters settled.",
      primaryAction: {
        labelKey: "phaseHighlight.expansionDraftAction",
        defaultLabel: "Protect players",
        path: ["protect_players"],
      },
      secondaryAction: {
        labelKey: "phaseHighlight.expansionDraftSecondaryAction",
        defaultLabel: "Review roster",
        path: ["roster"],
      },
    },
    [PHASE.FANTASY_DRAFT]: {
      phase,
      titleKey: "phaseHighlight.fantasyDraftTitle",
      defaultTitle: "Fantasy draft is on",
      descriptionKey: "phaseHighlight.fantasyDraftDesc",
      defaultDescription: "Pick the foundation of your new roster.",
      primaryAction: {
        labelKey: "phaseHighlight.fantasyDraftAction",
        defaultLabel: "Go to draft",
        path: ["draft"],
      },
    },
    [PHASE.PRESEASON]: {
      phase,
      titleKey: "phaseHighlight.preseasonTitle",
      defaultTitle: "Preseason",
      descriptionKey: "phaseHighlight.preseasonDesc",
      defaultDescription: "Check roster balance before opening night.",
      primaryAction: {
        labelKey: "phaseHighlight.preseasonAction",
        defaultLabel: "Review roster",
        path: ["roster"],
      },
      secondaryAction: {
        labelKey: "phaseHighlight.preseasonSecondaryAction",
        defaultLabel: "Find free agents",
        path: ["free_agents"],
      },
    },
    [PHASE.REGULAR_SEASON]: {
      phase,
      titleKey: "phaseHighlight.regularSeasonTitle",
      defaultTitle: "Regular season",
      descriptionKey: "phaseHighlight.regularSeasonDesc",
      defaultDescription: "Play or simulate the next day, then react.",
      primaryAction: {
        labelKey: "phaseHighlight.regularSeasonAction",
        defaultLabel: "Open today's schedule",
        path: ["daily_schedule"],
      },
      secondaryAction: {
        labelKey: "phaseHighlight.regularSeasonSecondaryAction",
        defaultLabel: "View standings",
        path: ["standings"],
      },
    },
    [PHASE.AFTER_TRADE_DEADLINE]: {
      phase,
      titleKey: "phaseHighlight.afterTradeTitle",
      defaultTitle: "After the trade deadline",
      descriptionKey: "phaseHighlight.afterTradeDesc",
      defaultDescription: "Trades are locked. Focus on the playoff push.",
      primaryAction: {
        labelKey: "phaseHighlight.afterTradeAction",
        defaultLabel: "View standings",
        path: ["standings"],
      },
      secondaryAction: {
        labelKey: "phaseHighlight.afterTradeSecondaryAction",
        defaultLabel: "Open schedule",
        path: ["daily_schedule"],
      },
    },
    [PHASE.PLAYOFFS]: {
      phase,
      titleKey: "phaseHighlight.playoffsTitle",
      defaultTitle: "Playoffs",
      descriptionKey: "phaseHighlight.playoffsDesc",
      defaultDescription: "Track your series and get to the next game.",
      primaryAction: {
        labelKey: "phaseHighlight.playoffsAction",
        defaultLabel: "Open playoff bracket",
        path: ["playoffs"],
      },
      secondaryAction: {
        labelKey: "phaseHighlight.playoffsSecondaryAction",
        defaultLabel: "Today's games",
        path: ["daily_schedule"],
      },
    },
    [PHASE.DRAFT_LOTTERY]: {
      phase,
      titleKey: "phaseHighlight.draftLotteryTitle",
      defaultTitle: "Draft lottery",
      descriptionKey: "phaseHighlight.draftLotteryDesc",
      defaultDescription: "See where your pick lands before draft night.",
      primaryAction: {
        labelKey: "phaseHighlight.draftLotteryAction",
        defaultLabel: "Open draft lottery",
        path: ["draft_lottery"],
      },
      secondaryAction: {
        labelKey: "phaseHighlight.draftLotterySecondaryAction",
        defaultLabel: "Scout prospects",
        path: ["draft_scouting"],
      },
    },
    [PHASE.DRAFT]: {
      phase,
      titleKey: "phaseHighlight.draftTitle",
      defaultTitle: "Draft night",
      descriptionKey: "phaseHighlight.draftDesc",
      defaultDescription: "Choose the next prospect for your roster.",
      primaryAction: {
        labelKey: "phaseHighlight.draftAction",
        defaultLabel: "Enter draft",
        path: ["draft"],
      },
    },
    [PHASE.AFTER_DRAFT]: {
      phase,
      titleKey: "phaseHighlight.afterDraftTitle",
      defaultTitle: "After the draft",
      descriptionKey: "phaseHighlight.afterDraftDesc",
      defaultDescription: "Review the class and finish roster cleanup.",
      primaryAction: {
        labelKey: "phaseHighlight.afterDraftAction",
        defaultLabel: "Review draft",
        path: ["draft"],
      },
      secondaryAction: {
        labelKey: "phaseHighlight.afterDraftSecondaryAction",
        defaultLabel: "Review roster",
        path: ["roster"],
      },
    },
    [PHASE.RESIGN_PLAYERS]: {
      phase,
      titleKey: "phaseHighlight.resignTitle",
      defaultTitle: "Re-sign your players",
      descriptionKey: "phaseHighlight.resignDesc",
      defaultDescription: "Decide who must stay before free agency opens.",
      primaryAction: {
        labelKey: "phaseHighlight.resignAction",
        defaultLabel: "Open negotiations",
        path: ["negotiation"],
      },
      secondaryAction: {
        labelKey: "phaseHighlight.resignSecondaryAction",
        defaultLabel: "Review roster",
        path: ["roster"],
      },
    },
    [PHASE.FREE_AGENCY]: {
      phase,
      titleKey: "phaseHighlight.freeAgencyTitle",
      defaultTitle: "Free agency",
      descriptionKey: "phaseHighlight.freeAgencyDesc",
      defaultDescription: "Fill roster holes without breaking your budget.",
      primaryAction: {
        labelKey: "phaseHighlight.freeAgencyAction",
        defaultLabel: "Browse free agents",
        path: ["free_agents"],
      },
      secondaryAction: {
        labelKey: "phaseHighlight.freeAgencySecondaryAction",
        defaultLabel: "Review roster",
        path: ["roster"],
      },
    },
  };

  return tasks[phase];
};

const makeRiskAlerts = ({
  contractStatus,
  injuredPlayers,
  injuredStarters,
  maxRosterSize,
  minRosterSize,
  payroll,
  payrollLimit,
  rosterCount,
  salaryCapType,
}: {
  contractStatus?: DashboardContractStatus;
  injuredPlayers?: number;
  injuredStarters?: number;
  maxRosterSize: number;
  minRosterSize: number;
  payroll?: number;
  payrollLimit?: number;
  rosterCount?: number;
  salaryCapType?: string;
}) => {
  const riskAlerts: RiskAlert[] = [];

  if (rosterCount !== undefined && rosterCount < minRosterSize) {
    riskAlerts.push({
      key: "rosterShort",
      severity: "danger",
      titleKey: "leagueDashboard.riskRosterShort",
      defaultTitle: "Roster is short",
      detailKey: "leagueDashboard.riskRosterShortDetail",
      defaultDetail: "{{count}}/{{min}} players signed",
      detailParams: { count: rosterCount, min: minRosterSize },
      // R1-3: filling a short roster is a get-help action. The UI resolves this
      // alert's concept ("roster") to the phase-aware market root
      // (resolveMarketRoot); this path is an inert fallback the queue overrides.
      path: ["roster"],
    });
  } else if (rosterCount !== undefined && rosterCount >= maxRosterSize) {
    riskAlerts.push({
      key: "rosterFull",
      severity: "info",
      titleKey: "leagueDashboard.riskRosterFull",
      defaultTitle: "Roster is full",
      detailKey: "leagueDashboard.riskRosterFullDetail",
      defaultDetail: "{{count}}/{{max}} roster spots used",
      detailParams: { count: rosterCount, max: maxRosterSize },
      path: ["roster"],
    });
  }

  if (injuredPlayers !== undefined && injuredPlayers > 0) {
    const hasInjuredStarter = injuredStarters !== undefined && injuredStarters > 0;
    riskAlerts.push({
      key: "injuries",
      severity: hasInjuredStarter ? "danger" : "warning",
      titleKey: "leagueDashboard.riskInjuries",
      defaultTitle: "Injuries need attention",
      detailKey: hasInjuredStarter
        ? "leagueDashboard.riskStartingInjuriesDetail"
        : "leagueDashboard.riskInjuriesDetail",
      defaultDetail: hasInjuredStarter
        ? "{{starterCount}} starter(s) are injured; adjust the next lineup"
        : "{{count}} injured player(s)",
      detailParams: {
        count: injuredPlayers,
        ...(hasInjuredStarter ? { starterCount: injuredStarters } : {}),
      },
      // R1-2: the injuries page became a roster-tab sheet; land on roster and
      // open the injuries sheet via the ?m_open=injuries intent channel.
      path: ["roster"],
      openSheet: "injuries",
    });
  }

  if (contractStatus?.kind === "actionable") {
    riskAlerts.push({
      key: "contracts",
      severity: "warning",
      titleKey: "leagueDashboard.riskContracts",
      defaultTitle: "Contracts expiring",
      detailKey: "leagueDashboard.riskContractsDetail",
      defaultDetail: "{{count}} player(s) need a decision",
      detailParams: { count: contractStatus.count },
      path: ["negotiation"],
    });
  }

  if (
    salaryCapType !== "none" &&
    payroll !== undefined &&
    payrollLimit !== undefined &&
    payroll > payrollLimit
  ) {
    riskAlerts.push({
      key: "salaryPressure",
      severity: "warning",
      titleKey: "leagueDashboard.riskSalary",
      defaultTitle: "Salary pressure",
      detailKey: "leagueDashboard.riskSalaryDetail",
      defaultDetail: "Payroll is over the current limit",
      path: ["team_finances"],
    });
  }

  return riskAlerts;
};

// G1b: expose the current-season owner trust (three-factor ownerMood) so the
// home coach-seat card can show it. Only the current value — the history curve
// stays on the Message page's OwnerMoodsChart. Mirrors updateOwnerMood.ts
// guards (no spectator / multi-team) and gracePeriodEnd read.
const updateOwnerMood = async (inputs: unknown, updateEvents: UpdateEvents) => {
  if (
    updateEvents.includes("firstRun") ||
    updateEvents.includes("gameSim") ||
    updateEvents.includes("newPhase")
  ) {
    if (g.get("spectator") || g.get("userTids").length > 1) {
      return { ownerMood: undefined };
    }

    const teamSeason = await idb.cache.teamSeasons.indexGet("teamSeasonsBySeasonTid", [
      g.get("season"),
      g.get("userTid"),
    ]);
    const mood = teamSeason?.ownerMood ?? { money: 0, playoffs: 0, wins: 0 };
    const total = mood.money + mood.playoffs + mood.wins;

    return {
      ownerMood: {
        inGracePeriod: g.get("season") < g.get("gracePeriodEnd"),
        money: mood.money,
        playoffs: mood.playoffs,
        total,
        wins: mood.wins,
      },
    };
  }
};

// N4-0: surface this season's owner expectation letter (if any) so the home
// coach-seat card can grade the season goal against the owner's ask and link to
// the letter. Absent (old saves / not yet sent) → the card falls back to the
// heuristic goal state.
const updateSeasonExpectations = async (inputs: unknown, updateEvents: UpdateEvents) => {
  if (
    updateEvents.includes("firstRun") ||
    updateEvents.includes("gameSim") ||
    updateEvents.includes("newPhase")
  ) {
    if (g.get("spectator") || g.get("userTids").length > 1) {
      return { seasonExpectation: undefined };
    }

    const season = g.get("season");
    const messages = await idb.getCopies.messages({}, "noCopyCache");

    // getCopies returns ascending mid (oldest first); take the latest letter
    // that matches the current season.
    let match;
    for (const message of messages) {
      if (message.expectations?.season === season) {
        match = message;
      }
    }

    return {
      seasonExpectation: match ? { ...match.expectations, mid: match.mid } : undefined,
    };
  }
};

// Recent league news for the home ticker and its in-place detail sheet.
const updateLeagueNews = async (inputs: unknown, updateEvents: UpdateEvents) => {
  if (
    updateEvents.includes("firstRun") ||
    updateEvents.includes("gameSim") ||
    updateEvents.includes("playerMovement") ||
    updateEvents.includes("newPhase")
  ) {
    const eventsAll = await idb.getCopies.events({ season: g.get("season") });
    eventsAll.reverse();
    const events = await processEvents(eventsAll, {
      level: "big",
      limit: HOME_NEWS_LIMIT,
    });

    return {
      news: events.map((event) => ({
        eid: event.eid,
        tids: event.tids,
        type: event.type,
        text: event.text,
        season: event.season,
      })),
    };
  }
};

const updateInbox = async (inputs: unknown, updateEvents: UpdateEvents) => {
  if (
    updateEvents.includes("firstRun") ||
    updateEvents.includes("gameSim") ||
    updateEvents.includes("newPhase")
  ) {
    // The dashboard only needs two lightweight rows for existing task logic, but
    // its unread badge must reflect the entire inbox rather than that preview.
    const allMessages = await idb.getCopies.messages(undefined, "noCopyCache");
    const messages = allMessages.slice(-2).reverse();
    return {
      messages: messages.map((message) => ({
        mid: message.mid,
        read: message.read,
        year: message.year,
        from: message.from,
      })),
      unreadMessageCount: allMessages.filter((message) => !message.read).length,
    };
  }
};

const updateTeam = async (inputs: unknown, updateEvents: UpdateEvents) => {
  if (
    updateEvents.includes("firstRun") ||
    updateEvents.includes("gameSim") ||
    updateEvents.includes("playerMovement") ||
    updateEvents.includes("newPhase")
  ) {
    const [t, latestSeason] = await Promise.all([
      idb.cache.teams.get(g.get("userTid")),
      idb.cache.teamSeasons.indexGet("teamSeasonsBySeasonTid", [g.get("season"), g.get("userTid")]),
    ]);

    const playoffRoundsWon = latestSeason?.playoffRoundsWon ?? -1;
    const playoffsByConf = await season.getPlayoffsByConf(g.get("season"));
    const numPlayoffRounds = g.get("numGamesPlayoffSeries", "current").length;
    const roundsWonText = helpers.upperCaseFirstLetter(
      helpers.roundsWonText({
        playoffRoundsWon,
        numPlayoffRounds,
        playoffsByConf,
      }),
    );

    return {
      region: t?.region ?? "",
      name: t?.name ?? "",
      won: latestSeason?.won ?? 0,
      lost: latestSeason?.lost ?? 0,
      tied: latestSeason?.tied ?? 0,
      otl: latestSeason?.otl ?? 0,
      cash: latestSeason ? latestSeason.cash / 1000 : 0,
      salaryCap: g.get("salaryCap") / 1000,
      salaryCapType: g.get("salaryCapType"),
      luxuryPayroll: g.get("luxuryPayroll") / 1000,
      season: g.get("season"),
      playoffRoundsWon,
      roundsWonText,
      userTid: g.get("userTid"),
    };
  }
};

const updatePayroll = async (inputs: unknown, updateEvents: UpdateEvents) => {
  if (updateEvents.includes("firstRun") || updateEvents.includes("playerMovement")) {
    const payroll = await team.getPayroll(g.get("userTid"));
    return {
      payroll: payroll / 1000, // [millions of dollars]
    };
  }
};

const updateTeams = async (inputs: unknown, updateEvents: UpdateEvents) => {
  if (
    updateEvents.includes("firstRun") ||
    updateEvents.includes("gameSim") ||
    updateEvents.includes("playerMovement") ||
    updateEvents.includes("newPhase")
  ) {
    const stats = ["pts", "oppPts", "trb", "ast"] as const;
    const statNames = ["Points", "Allowed", "Rebounds", "Assists"];
    const teams = await idb.getCopies.teamsPlus(
      {
        attrs: ["tid"],
        seasonAttrs: [
          "won",
          "lost",
          "otl",
          "tied",
          "winp",
          "pts",
          "att",
          "revenue",
          "profit",
          "cid",
          "did",
          "wonDiv",
          "lostDiv",
          "tiedDiv",
          "otlDiv",
          "wonConf",
          "lostConf",
          "tiedConf",
          "otlConf",
        ],
        stats: ["pts", "oppPts", "gp"] as const,
        season: g.get("season"),
        showNoStats: true,
      },
      "noCopyCache",
    );
    const t = teams.find((t2) => t2.tid === g.get("userTid"));
    const cid = t !== undefined ? t.seasonAttrs.cid : undefined;
    let att = 0;
    let rank = 1;
    let revenue = 0;
    let profit = 0;
    let teamStats: {
      name: string;
      rank: number;
      stat: string;
      value: number;
    }[] = [];

    const playoffsByConf = await season.getPlayoffsByConf(g.get("season"));
    const teamsRank = await orderTeams(
      (teams as any).filter((t: any) => playoffsByConf === false || t.seasonAttrs.cid === cid),
      teams as any,
    );

    for (const t2 of teamsRank) {
      if (playoffsByConf === false || t2.seasonAttrs.cid === cid) {
        if (t2.tid === g.get("userTid")) {
          teamStats = stats.map((stat, i) => {
            return {
              name: statNames[i]!,
              rank: 0,
              stat,
              value: (t2.stats as any)[stat],
            };
          });
          att = (t2.seasonAttrs as any).att;
          revenue = (t2.seasonAttrs as any).revenue;
          profit = (t2.seasonAttrs as any).profit;
          break;
        } else {
          rank += 1;
        }
      }
    }

    for (const stat of stats) {
      teams.sort((a, b) => (b.stats as any)[stat] - (a.stats as any)[stat]);

      for (const [j, t] of teams.entries()) {
        if (t.tid === g.get("userTid")) {
          const entry = teamStats.find((teamStat) => teamStat.stat === stat);

          if (entry) {
            entry.rank = j + 1;

            if (stat.startsWith("opp")) {
              entry.rank = teams.length + 1 - entry.rank;
            }
          }

          break;
        }
      }
    }

    return {
      att,
      rank,
      revenue,
      profit,
      teamStats,
    };
  }
};

const updatePlayers = async (inputs: unknown, updateEvents: UpdateEvents) => {
  if (
    updateEvents.includes("firstRun") ||
    updateEvents.includes("gameSim") ||
    updateEvents.includes("playerMovement") ||
    updateEvents.includes("newPhase")
  ) {
    const startersStats = ["gp", "min", "pts", "trb", "ast", "per"];
    const leaderStats = ["pts", "trb", "ast"];
    const userTid = g.get("userTid");
    const [playersAll, negotiations] = await Promise.all([
      idb.cache.players.indexGetAll("playersByTid", [PLAYER.FREE_AGENT, Infinity]),
      idb.cache.negotiations.getAll(),
    ]);

    const userPlayers = addFirstNameShort(
      await idb.getCopies.playersPlus(
        playersAll.filter((p) => p.tid === userTid),
        {
          attrs: [
            "pid",
            "firstName",
            "lastName",
            "abbrev",
            "tid",
            "age",
            "contract",
            "draft",
            "rosterOrder",
            "injury",
            "watch",
            "jerseyNumber",
          ],
          ratings: ["ovr", "pot", "dovr", "dpot", "skills", "pos"],
          stats: [...startersStats, ...leaderStats, "yearsWithTeam"],
          season: g.get("season"),
          showNoStats: true,
          showRookies: true,
          fuzz: true,
          tid: userTid,
        },
      ),
    );

    // Roster
    // Find starters or top 5 players
    const numPlayersOnCourt = g.get("numPlayersOnCourt");
    userPlayers.sort((a, b) => a.rosterOrder - b.rosterOrder);
    const starters = userPlayers.slice(0, Math.max(5, numPlayersOnCourt));
    const rosterCount = userPlayers.length;
    const injuredPlayers = userPlayers.filter((p) => p.injury.gamesRemaining > 0).length;
    const injuredStarters = starters.filter((p) => p.injury.gamesRemaining > 0).length;
    const contractExpiringCount = userPlayers.filter(
      (p) => p.contract.exp <= g.get("season"),
    ).length;
    const pendingNegotiationCount = negotiations.filter(
      (negotiation) => negotiation.tid === userTid,
    ).length;
    const contractStatus = getDashboardContractStatus({
      contractExpiringCount,
      pendingNegotiationCount,
      phase: g.get("phase"),
    });
    const teamOvr = team.ovr(userPlayers, {
      playoffs: g.get("phase") === PHASE.PLAYOFFS,
    });

    return {
      challengeNoRatings: g.get("challengeNoRatings"),
      contractExpiringCount,
      contractStatus,
      injuredPlayers,
      injuredStarters,
      numPlayersOnCourt,
      pendingNegotiationCount,
      rosterCount,
      teamOvr,
      starters,
      startersStats,
    };
  }
};

const updateScheduleSummary = async (inputs: unknown, updateEvents: UpdateEvents) => {
  if (
    updateEvents.includes("firstRun") ||
    updateEvents.includes("gameSim") ||
    updateEvents.includes("newPhase")
  ) {
    const schedule = await season.getSchedule();
    const todayDay = schedule[0]?.day;
    const todayGames = todayDay === undefined ? [] : await getUpcoming({ day: todayDay });
    const userTid = g.get("userTid");
    const todayUserGame = todayGames.find((game) =>
      game.teams.some((candidate) => candidate.tid === userTid),
    );
    const nextGame =
      todayUserGame ??
      (
        await getUpcoming({
          onlyOneGame: true,
          tid: userTid,
        })
      )[0];

    return {
      nextGame,
    };
  }
};

// C1: last completed game for the home-page score card. Compact summary only —
// scores per team plus the user team's key performer — never the raw players
// arrays from the box score.
const updateLastGame = async (inputs: unknown, updateEvents: UpdateEvents) => {
  if (
    updateEvents.includes("firstRun") ||
    updateEvents.includes("gameSim") ||
    updateEvents.includes("newPhase")
  ) {
    const userTid = g.get("userTid");
    const games = await getProcessedGames({
      tid: userTid,
      season: g.get("season"),
    });

    // getProcessedGames iterates backwards, so the first entry is the most
    // recent completed game.
    const game = games[0];

    if (!game) {
      return { lastGame: undefined };
    }

    const userIndex = game.teams[0]!.tid === userTid ? 0 : 1;
    const userTeam = game.teams[userIndex]!;
    const best = Array.isArray(userTeam.players)
      ? getBestPlayerBoxScore(userTeam.players)
      : undefined;

    // G3-1: credit the human GM for a recently traded-for / signed player who
    // suited up. Read-only derivation over the roster's transactions + stats
    // rows; getProcessedGames already scoped this to the current season.
    const rosterPlayers = await idb.cache.players.indexGetAll("playersByTid", userTid);
    const attribution = deriveGameAttribution({
      userTeamPlayers: userTeam.players,
      players: new Map(rosterPlayers.map((p) => [p.pid, p])),
      season: g.get("season"),
      userTid,
    });

    return {
      lastGame: {
        attribution,
        gid: game.gid,
        numPeriods: game.numPeriods,
        overtimes: game.overtimes,
        season: g.get("season"),
        teams: [
          {
            pts: game.teams[0]!.pts,
            sPts: (game.teams[0] as any).sPts,
            tid: game.teams[0]!.tid,
          },
          {
            pts: game.teams[1]!.pts,
            sPts: (game.teams[1] as any).sPts,
            tid: game.teams[1]!.tid,
          },
        ] as [
          { pts: number; sPts?: number; tid: number },
          { pts: number; sPts?: number; tid: number },
        ],
        keyPlayer: best
          ? {
              name: best.p.name,
              pid: best.p.pid,
              pos: best.p.pos,
              stats: best.statTexts.slice(0, 3).map((stat) => ({
                stat,
                value: best.processedStats[stat],
              })),
            }
          : undefined,
      },
    };
  }
};

const updatePlayoffs = async (inputs: unknown, updateEvents: UpdateEvents) => {
  if (
    updateEvents.includes("firstRun") ||
    (g.get("phase") >= PHASE.PLAYOFFS && updateEvents.includes("gameSim")) ||
    (updateEvents.includes("newPhase") &&
      (g.get("phase") === PHASE.PLAYOFFS || g.get("phase") === PHASE.PRESEASON))
  ) {
    const playoffSeries = await idb.getCopy.playoffSeries({
      season: g.get("season"),
    });
    let foundSeries;
    let seriesTitle = "";
    let showPlayoffSeries = false;
    let numGamesToWinSeries = 4;

    if (playoffSeries !== undefined && playoffSeries.series.length > 0) {
      const series = playoffSeries.series;
      await helpers.augmentSeries(series);

      // Find the latest playoff series with the user's team in it

      let found = false;

      const playoffsByConf = await season.getPlayoffsByConf(g.get("season"));
      const numPlayoffRounds = g.get("numGamesPlayoffSeries", "current").length;

      // This is needed since currentRound is -1 for a play-in
      const lastRound = playoffSeries.currentRound >= 0 ? playoffSeries.currentRound : 0;

      for (let rnd = lastRound; rnd >= 0; rnd--) {
        const roundSeries = series[rnd];
        if (roundSeries) {
          for (const [i, { away, home }] of roundSeries.entries()) {
            if (
              home.tid === g.get("userTid") ||
              (away && away.tid === g.get("userTid") && !away.pendingPlayIn)
            ) {
              foundSeries = roundSeries[i];
              found = true;
              showPlayoffSeries = true;

              seriesTitle = helpers.upperCaseFirstLetter(
                helpers.playoffRoundName(rnd, numPlayoffRounds, playoffsByConf),
              );
              numGamesToWinSeries = helpers.numGamesToWinSeries(
                g.get("numGamesPlayoffSeries", "current")[rnd],
              );
              break;
            }
          }

          if (found) {
            break;
          }
        }
      }

      const playIns = playoffSeries.playIns;
      if (!found && playIns) {
        await helpers.augmentSeries(playIns);
        for (const playIn of playIns) {
          for (let i = playIn.length - 1; i >= 0; i--) {
            const { away, home } = playIn[i]!;
            if (home.tid === g.get("userTid") || away.tid === g.get("userTid")) {
              foundSeries = playIn[i];
              found = true;
              showPlayoffSeries = true;

              seriesTitle = "Play-in tournament";

              numGamesToWinSeries = 1;
              break;
            }
          }

          if (found) {
            break;
          }
        }
      }
    }

    return {
      numGamesToWinSeries,
      series: foundSeries,
      seriesTitle,
      showPlayoffSeries,
    };
  }
};

const updateStandings = async (inputs: unknown, updateEvents: UpdateEvents) => {
  if (updateEvents.includes("firstRun") || updateEvents.includes("gameSim")) {
    const teams = await idb.getCopies.teamsPlus(
      {
        attrs: ["tid"],
        seasonAttrs: [
          "won",
          "lost",
          "tied",
          "otl",
          "wonDiv",
          "lostDiv",
          "tiedDiv",
          "otlDiv",
          "wonConf",
          "lostConf",
          "tiedConf",
          "otlConf",
          "winp",
          "pts",
          "cid",
          "did",
          "abbrev",
          "region",
          "clinchedPlayoffs",
          "imgURL",
          "imgURLSmall",
        ],
        stats: ["pts", "oppPts", "gp"],
        season: g.get("season"),
        showNoStats: true,
      },
      "noCopyCache",
    );

    // Find user's conference
    const userTid = g.get("userTid");
    const cid = teams.find((t) => t.tid === userTid)?.seasonAttrs.cid;

    const playoffsByConf = await season.getPlayoffsByConf(g.get("season"));

    let confOrAllTeams = (
      await orderTeams(
        teams.filter((t) => !playoffsByConf || t.seasonAttrs.cid === cid),
        teams,
      )
    ).map((t) => ({
      ...t,
      rank: 0,
      gb: 0,
    }));

    const pointsFormula = g.get("pointsFormula", "current");
    const usePts = pointsFormula !== "";

    let rank = 1;
    for (const t of confOrAllTeams) {
      if (!playoffsByConf || t.seasonAttrs.cid === cid) {
        t.rank = rank;
        if (!usePts) {
          t.gb = rank === 1 ? 0 : helpers.gb(confOrAllTeams[0]!.seasonAttrs, t.seasonAttrs);
        }

        rank += 1;
      }
    }

    // Keep only up to 16 teams or else it looks ugly, and always keep userTid
    const NUM_TEAMS_TO_SHOW = 16;
    let seenUserTid = false;
    confOrAllTeams = confOrAllTeams.filter((t) => {
      if (t.tid === userTid) {
        seenUserTid = true;
        return true;
      }

      if (seenUserTid) {
        return t.rank <= NUM_TEAMS_TO_SHOW;
      } else {
        return t.rank < NUM_TEAMS_TO_SHOW;
      }
    });

    const { maxPlayoffSeed, maxPlayoffSeedNoPlayIn } = await getMaxPlayoffSeed(
      g.get("season"),
      playoffsByConf,
    );

    return {
      confOrAllTeams,
      maxPlayoffSeed,
      maxPlayoffSeedNoPlayIn,
      playoffsByConf,
      pointsFormula,
      usePts,
    };
  }
};

export default async (inputs: unknown, updateEvents: UpdateEvents) => {
  // Woo TypeScript, gotta break this up into 3 parts or it just says fuck it and calls it any
  const part1 = Object.assign(
    {},
    await updateInbox(inputs, updateEvents),
    await updateTeam(inputs, updateEvents),
    await updatePayroll(inputs, updateEvents),
    await updateOwnerMood(inputs, updateEvents),
    await updateSeasonExpectations(inputs, updateEvents),
    await updateLeagueNews(inputs, updateEvents),
  );
  const part2 = Object.assign({}, await updateTeams(inputs, updateEvents));
  const part3 = Object.assign(
    {},
    await updatePlayers(inputs, updateEvents),
    await updateScheduleSummary(inputs, updateEvents),
    await updateLastGame(inputs, updateEvents),
    await updatePlayoffs(inputs, updateEvents),
    await updateStandings(inputs, updateEvents),
  );

  const phase = g.get("phase");
  const merged = Object.assign({}, part1, part2, part3, {
    phase,
    phaseTask: getPhaseTask(phase),
  }) as Record<string, any>;
  const payrollLimit = merged.salaryCapType === "none" ? merged.luxuryPayroll : merged.salaryCap;

  return Object.assign(merged, {
    lastGame: merged.lastGame,
    nextGame: merged.nextGame,
    ownerMood: merged.ownerMood,
    // N4-0: this season's owner expectation (with the letter's mid), or
    // undefined when no letter exists (old saves / spectator).
    seasonExpectation: merged.seasonExpectation,
    // Recent league news for the home ticker + detail sheet.
    news: merged.news as
      | { eid: number; tids?: number[]; type: string; text: string; season: number }[]
      | undefined,
    phase,
    phaseTask: merged.phaseTask,
    // N4-0 ③: raw signals the home phase checklist derives completion from.
    contractExpiringCount: merged.contractExpiringCount,
    injuredPlayers: merged.injuredPlayers,
    injuredStarters: merged.injuredStarters,
    minRosterSize: g.get("minRosterSize"),
    maxRosterSize: g.get("maxRosterSize"),
    riskAlerts: makeRiskAlerts({
      contractStatus: merged.contractStatus,
      injuredPlayers: merged.injuredPlayers,
      injuredStarters: merged.injuredStarters,
      maxRosterSize: g.get("maxRosterSize"),
      minRosterSize: g.get("minRosterSize"),
      payroll: merged.payroll,
      payrollLimit,
      rosterCount: merged.rosterCount,
      salaryCapType: merged.salaryCapType,
    }),
    teamSnapshot: {
      capSpace:
        merged.payroll !== undefined && payrollLimit !== undefined
          ? payrollLimit - merged.payroll
          : undefined,
      payroll: merged.payroll,
      payrollLimit,
      rank: merged.rank,
      record: {
        lost: merged.lost,
        otl: merged.otl,
        tied: merged.tied,
        won: merged.won,
      },
      rosterCount: merged.rosterCount,
      teamOvr: merged.teamOvr,
    },
  });
};
